import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyBalanceTransactionKey,
  ShopifyBalanceTransactionToPayoutKey,
  ShopifyPayoutKey,
  ShopifyPayoutToBalanceTransactionsKey,
  ShopifyPayoutToShopKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyPayoutKey,
  "2022-01": {
    name: "Shopify Payout",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Status",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "status" } },
      },
      {
        name: "Date",
        fieldType: FieldType.DateTime,
        configuration: {
          includeTime: false,
        },
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "date" } },
      },
      {
        name: "Currency",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "currency" } },
      },
      {
        name: "Amount",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "amount" } },
      },
      {
        name: "Summary",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "summary" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyPayoutToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
      {
        name: "Balance Transactions",
        fieldType: FieldType.HasMany,
        key: ShopifyPayoutToBalanceTransactionsKey,
        configuration: {
          relatedModelKey: ShopifyBalanceTransactionKey,
          inverseFieldKey: ShopifyBalanceTransactionToPayoutKey,
        },
      },
    ],
    syncConfig: { type: "rest_list", resource: "payout" },
  },
  "2025-04": {
    graphQLTypename: "ShopifyPaymentsPayout",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "status", graphQLFieldKey: "status", isShopifyEnum: true } },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "CANCELED" }, { name: "FAILED" }, { name: "PAID" }, { name: "SCHEDULED" }, { name: "IN_TRANSIT" }],
        },
      },
      {
        name: "Date",
        fieldType: FieldType.DateTime,
        configuration: {
          includeTime: false,
        },
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "date" } },
        isDeleted: true,
      },
      {
        name: "Currency",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "currency" } },
        isDeleted: true,
      },
      {
        name: "Amount",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "amount" } },
        isDeleted: true,
      },
      {
        name: "Summary",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "summary",
            graphQLFieldKey: "summary",
            graphQLSelection: `
          adjustmentsFee {
            amount
            currencyCode
          }
          adjustmentsGross {
            amount
            currencyCode
          }
          advanceFees {
            amount
            currencyCode
          }
          adjustmentsGross {
            amount
            currencyCode
          }
          advanceFees {
            amount
            currencyCode
          }
          advanceGross {
            amount
            currencyCode
          }
          chargesFee {
            amount
            currencyCode
          }
          chargesGross {
            amount
            currencyCode
          }
          refundsFee {
            amount
            currencyCode
          }
          refundsFeeGross {
            amount
            currencyCode
          }
          reservedFundsFee {
            amount
            currencyCode
          }
          reservedFundsGross {
            amount
            currencyCode
          }
          retriedPayoutsFee {
            amount
            currencyCode
          }
          retriedPayoutsGross {
            amount
            currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Balance Transactions",
        fieldType: FieldType.HasMany,
        key: ShopifyPayoutToBalanceTransactionsKey,
        configuration: {
          relatedModelKey: ShopifyBalanceTransactionKey,
          inverseFieldKey: ShopifyBalanceTransactionToPayoutKey,
        },
      },
      {
        name: "Issued At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "issuedAt" } },
      },
      {
        name: "Net",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "net",
            graphQLSelection: `
          amount
          currencyCode
          `,
          },
        },
      },
      {
        name: "Legacy Resource Id",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "legacyResourceId" } },
      },
      {
        name: "Transaction Type",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "transactionType", isShopifyEnum: true } },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "DEPOSIT" }, { name: "WITHDRAWAL" }],
        },
      },
    ],
    addedRequiredScopes: ["read_shopify_payments_accounts"],
    syncConfig: {
      type: "graphql_list",
      resource: "payouts",
      filters: {},
    },
  },
  "2025-10": {
    fields: [
      {
        name: "External Trace Id",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "externalTraceId" } },
      },
    ],
  },
};

export const ShopifyPayout = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
